import { Button } from "@chakra-ui/button";
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/modal";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { CherryHubAdminApi } from "../api/adminApiModels";
import { Checkbox, SkeletonText, VStack, Text, HStack } from "@chakra-ui/react";
import { useSearchInput } from "./useSearchInput";
import { useAdminApiRequest } from "./useApiRequest";
import { cloneDeep } from "lodash";

interface PromptState {
  resolve: (result: {
    ok: boolean;
    keyValuePairs?: { Key: string; Value: string }[];
  }) => void;
  reject: () => void;
  businessId: string;
  integrationId: string;
  fieldIdentifier: string;
  existingKeys: string[];
  action?: string;
}

export function useRegistrationKeyValuePairsModal() {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [activePrompt, setActivePrompt] = useState<PromptState | null>(null);
  const [activeId, setActiveId] =
    useState<CherryHubAdminApi.CustomFormField | null>(null);
  const [kvpToBeAdded, setKvpToBeAdded] = useState<
    { Key: string; Value: string; isChecked: boolean }[]
  >([]);

  const { data: kvpOptions, isLoading: kvpOptionsLoading } = useAdminApiRequest(
    (apiClient) =>
      activePrompt
        ? apiClient.getMMSFormFieldKeyValuePairs(
            activePrompt.businessId,
            activePrompt.integrationId,
            activePrompt.fieldIdentifier
          )
        : null,
    [activePrompt]
  );

  useEffect(() => {
    setKvpToBeAdded(
      kvpOptions
        ?.filter(
          (kvp) => !activePrompt?.existingKeys.find((key) => key == kvp.Key)
        )
        ?.map((kvp) => {
          return {
            Key: kvp.Key,
            Value: kvp.Value,
            isChecked: false,
          };
        }) ?? []
    );
  }, [kvpOptions, kvpOptionsLoading]);

  const onClickOk = useCallback(() => {
    if (activePrompt) {
      activePrompt.resolve({
        ok: true,
        keyValuePairs: kvpToBeAdded.filter((kvp) => kvp.isChecked),
      });
      setActivePrompt(null);
      setKvpToBeAdded([]);
    }
  }, [activePrompt, setActivePrompt, kvpToBeAdded]);

  const onCancel = useCallback(() => {
    if (activePrompt) {
      activePrompt.resolve({ ok: false });
      setActivePrompt(null);
      setKvpToBeAdded([]);
    }
  }, [activePrompt, setActivePrompt]);

  const {
    debouncedQuery,
    isDebouncing,
    onChange: onChangeSearch,
    query,
    element: searchInputElement,
  } = useSearchInput({ placeholder: "Search field...", mb: 2 });

  const tickAll = (checked: boolean) => {
    filteredToBeAdded().forEach((kvp) => {
      kvp.isChecked = checked;
    });
    setKvpToBeAdded(cloneDeep(kvpToBeAdded));
  };

  const filteredToBeAdded = () => {
    return kvpToBeAdded.filter((kvp) =>
      debouncedQuery
        ? kvp.Key.toLocaleLowerCase().indexOf(
            debouncedQuery.toLocaleLowerCase()
          ) != -1 ||
          kvp.Value.toLocaleLowerCase().indexOf(
            debouncedQuery.toLocaleLowerCase()
          ) != -1
        : true
    );
  };

  const modalElement = useMemo(() => {
    return (
      <Modal
        isOpen={!!activePrompt}
        onClose={onCancel}
        initialFocusRef={cancelRef}
        size="4xl"
      >
        <ModalOverlay>
          <ModalContent>
            <ModalHeader>
              Add Key Value Pairs from {activePrompt?.fieldIdentifier}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody overflowY="auto" maxH="700px">
              <VStack w="100%" alignItems="flex-start" spacing={4}>
                {searchInputElement}
                {kvpOptionsLoading && (
                  <SkeletonText noOfLines={4} spacing={4} w="100%" />
                )}
                {!kvpOptionsLoading && !kvpToBeAdded.length && (
                  <Text fontStyle="italic" fontSize="sm" alignSelf="center">
                    No key value pairs.
                  </Text>
                )}
                {!kvpOptionsLoading &&
                  filteredToBeAdded().map((kvp) => (
                    <Checkbox
                      key={kvp.Key}
                      colorScheme="green"
                      isChecked={kvp.isChecked}
                      onChange={(e) => {
                        kvp.isChecked = e.target.checked;
                        setKvpToBeAdded(cloneDeep(kvpToBeAdded));
                      }}
                    >{`${kvp.Key} - ${kvp.Value}`}</Checkbox>
                  ))}
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button onClick={() => tickAll(true)}>Check All</Button>
              <Button ml={3} mr="auto" onClick={() => tickAll(false)}>
                Uncheck All
              </Button>
              <Button ref={cancelRef} onClick={onCancel}>
                Cancel
              </Button>
              <Button colorScheme="cherryButton" ml={3} onClick={onClickOk}>
                {activePrompt?.action ?? "Add"}
              </Button>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      </Modal>
    );
  }, [
    activePrompt,
    cancelRef,
    onCancel,
    onClickOk,
    kvpToBeAdded,
    query,
    debouncedQuery,
    activeId,
    kvpOptions,
    kvpOptionsLoading,
  ]);

  const showKeyValuePairsModal = useCallback(
    (
      businessId: string,
      integrationId: string,
      fieldIdentifier,
      existingKeys,
      action?: string
    ): Promise<{
      ok: boolean;
      keyValuePairs?: { Key: string; Value: string }[];
    }> => {
      const promise = new Promise<{
        ok: boolean;
        keyValuePairs?: { Key: string; Value: string }[];
      }>((resolve, reject) => {
        setActivePrompt({
          resolve,
          reject,
          businessId,
          integrationId,
          fieldIdentifier,
          existingKeys,
          action,
        });
      }).then((result) => {
        return result;
      });

      return promise;
    },
    [setActivePrompt]
  );

  return { modalElement, showKeyValuePairsModal };
}
